'use client';

import { SOCKET_RETRY_DELAY } from '@/utils/constant';
import { createContext, ReactNode, useContext, useEffect, useMemo, useRef, useState } from 'react';
import io, { Socket } from 'socket.io-client';

/* -------------------- helpers -------------------- */
const getAccessTokenFromCookie = (): string | undefined => {
  if (typeof document === 'undefined') return undefined;
  return document.cookie.split('; ').find(row => row.startsWith('access-token='))?.split('=')[1];
};
interface SocketContextType {
  socket: Socket | null;
  isConnected: boolean;
  reconnectWithNewToken: () => void;
}
interface ModuleListeners {
  [moduleId: string]: {
    userName: string;
    callback: (users: SocketUser[]) => void;
  }[];
}
interface SocketUser {
  id: string;
  name: string;
  email: string;
  shortName: string;
}
interface SocketContextType {
  socket: Socket | null;
  listenToModuleConnection: (moduleName: string, userName: string, setSocketUserList: (users: SocketUser[]) => void) => void;
  leaveModule: (moduleName: string, user: SocketUser) => void;
  leaveAllModules: (user: SocketUser) => void;
  joinModule: (moduleName: string, user: SocketUser) => void;
  getUsersInRoom: (moduleName: string, userName: string, setSocketUserList: (users: SocketUser[]) => void) => void;
  reconnectWithNewToken: () => void;
}
const SocketContext = createContext<SocketContextType | null>(null);
const notifyModuleListeners = (listeners: {
  userName: string;
  callback: (users: SocketUser[]) => void;
}[], users: SocketUser[]) => {
  listeners.forEach(({
    userName,
    callback
  }) => {
    const filteredUsers = users.filter(user => user.name && user.name.trim() !== userName.trim());
    callback(filteredUsers);
  });
};
interface SocketProviderProps {
  children: ReactNode;
}
export const SocketProvider = ({
  children
}: SocketProviderProps) => {
  const [isConnected, setIsConnected] = useState<boolean>(false); // Add this
  const socketUrl = process.env.NEXT_PUBLIC_SOCKET_BACKEND_URL;
  const socketRef = useRef<Socket | null>(null); // Add this
  const listenersSetupRef = useRef<boolean>(false); // Add this
  const moduleListenersRef = useRef<ModuleListeners>({});
  const reconnectWithNewToken = () => {
    if (socketRef.current) {
      socketRef.current.disconnect();
      const token = getAccessTokenFromCookie();
      socketRef.current.auth = {
        token
      };
      socketRef.current.connect();
    }
  };
  useEffect(() => {
    if (!socketUrl) {
      return () => {};
    }

    // Only create new socket if we don't have one
    if (!socketRef.current) {
      const token = getAccessTokenFromCookie();
      const newSocket = io(socketUrl, {
        transports: ['websocket'],
        auth: {
          token
        },
        withCredentials: true,
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        timeout: 200000
      });
      socketRef.current = newSocket;
    }

    // Setup event listeners only once
    if (!listenersSetupRef.current && socketRef.current) {
      const currentSocket = socketRef.current;
      currentSocket.on('connect', () => {
        setIsConnected(true);
      });
      currentSocket.on('disconnect', _reason => {
        setIsConnected(false);
      });
      currentSocket.on('connect_error', _error => {
        setIsConnected(false);
      });
      currentSocket.on('reconnect', _attemptNumber => {
        setIsConnected(true);
      });
      currentSocket.on('moduleStatus', ({
        module,
        _userCount,
        users
      }) => {
        // Notify all listeners registered for this module
        if (moduleListenersRef.current[module]) {
          notifyModuleListeners(moduleListenersRef.current[module], users);
        }
      });
      listenersSetupRef.current = true;
    }

    // Cleanup function
    return () => {};
  }, [socketUrl]);
  const listenToModuleConnection = (moduleName: string, userName: string, setSocketUserList: (users: SocketUser[]) => void): void => {
    if (!socketRef.current) {
      return;
    }

    // Initialize module listeners array if it doesn't exist
    if (!moduleListenersRef.current[moduleName]) {
      moduleListenersRef.current[moduleName] = [];
    }

    // Check if this listener is already registered
    const existingListenerIndex = moduleListenersRef.current[moduleName].findIndex(listener => listener.callback === setSocketUserList);

    // If not registered, add it
    if (existingListenerIndex === -1) {
      moduleListenersRef.current[moduleName].push({
        userName,
        callback: setSocketUserList
      });
    } else {
      // Update existing listener
      moduleListenersRef.current[moduleName][existingListenerIndex].userName = userName;
    }
  };
  const leaveModule = (moduleName: string, user: SocketUser): void => {
    if (socketRef.current && isConnected) {
      socketRef.current.emit('leaveModule', {
        moduleName,
        user
      });

      // Remove listeners for this user and module
      if (moduleListenersRef.current[moduleName]) {
        moduleListenersRef.current[moduleName] = moduleListenersRef.current[moduleName].filter(listener => listener.userName !== user.name);
      }
    }
  };
  const leaveAllModules = (user: SocketUser): void => {
    if (socketRef.current && isConnected) {
      socketRef.current.emit('leaveAllModules', {
        user
      });

      // Clear all listeners for this user
      Object.keys(moduleListenersRef.current).forEach(moduleName => {
        moduleListenersRef.current[moduleName] = moduleListenersRef.current[moduleName].filter(listener => listener.userName !== user.name);
      });
      socketRef.current.disconnect();
      setIsConnected(false);
    }
  };
  const joinModule = (moduleName: string, user: SocketUser): void => {
    if (socketRef.current && isConnected) {
      socketRef.current.emit('joinModule', {
        moduleName,
        user
      });
    }
  };
  const getUsersInRoom = (moduleName: string, userName: string, setSocketUserList: (users: SocketUser[]) => void): void => {
    if (socketRef.current) {
      socketRef.current.emit('getUsersInRoom', moduleName);
      listenToModuleConnection(moduleName, userName, setSocketUserList);
    }

    // Retry after a delay if disconnected
    if (socketRef.current && !isConnected) {
      setTimeout(() => {
        if (socketRef.current?.connected) {
          getUsersInRoom(moduleName, userName, setSocketUserList);
        }
      }, SOCKET_RETRY_DELAY);
    }
  };
  const contextValue = useMemo(() => ({
    socket: socketRef.current,
    isConnected,
    listenToModuleConnection,
    leaveModule,
    leaveAllModules,
    joinModule,
    getUsersInRoom,
    reconnectWithNewToken
  }), [isConnected]);
  return <SocketContext.Provider value={contextValue} data-sentry-element="unknown" data-sentry-component="SocketProvider" data-sentry-source-file="socketContext.tsx">{children}</SocketContext.Provider>;
};
export const useSocket = (): SocketContextType => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};